import React, { useMemo, useState } from 'react';
import { Card, Col, Row, Skeleton, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { DateRangeFilter, DateRange, defaultDateRange } from '@shared/components/DateRangeFilter';
import { LineChartCard, ChartSeries } from '@shared/components/charts/LineChartCard';
import { useAnalyticsSponsors } from '@shared/api/hooks/useAnalyticsSponsors';

function pivotTimeline(
  timeline: Array<{ date: string; sponsorId: string; count: number }>,
  sponsorNames: Record<string, string>,
): { data: Array<Record<string, string | number>>; series: ChartSeries[] } {
  const dates = Array.from(new Set(timeline.map((t) => t.date))).sort();
  const sponsorIds = Array.from(new Set(timeline.map((t) => t.sponsorId)));

  const data = dates.map((date) => {
    const row: Record<string, string | number> = { date: dayjs(date).format('DD/MM') };
    for (const sid of sponsorIds) {
      row[sid] = timeline.find((t) => t.date === date && t.sponsorId === sid)?.count ?? 0;
    }
    return row;
  });

  const series = sponsorIds.map((sid) => ({ key: sid, label: sponsorNames[sid] ?? sid }));
  return { data, series };
}

export function SponsorsPage(): React.ReactElement {
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const { data, isLoading } = useAnalyticsSponsors(range);

  const sponsorNames = useMemo(
    () =>
      Object.fromEntries(
        (data?.sponsors ?? [])
          .filter((s): s is typeof s & { sponsorId: string } => s.sponsorId !== null)
          .map((s) => [s.sponsorId, s.name]),
      ),
    [data?.sponsors],
  );
  const { data: chartData, series } = useMemo(
    () => pivotTimeline(data?.timeline ?? [], sponsorNames),
    [data?.timeline, sponsorNames],
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0, fontStyle: 'italic' }}>
            Sponsor
          </Typography.Title>
          <Typography.Text type="secondary">Scontrini e tifosi per sponsor</Typography.Text>
        </div>
        <DateRangeFilter value={range} onChange={setRange} />
      </Space>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title="Sponsor">
              <Table
                size="small"
                rowKey={(r) => r.sponsorId ?? 'no-sponsor'}
                dataSource={data?.sponsors ?? []}
                pagination={false}
                columns={[
                  {
                    title: 'Sponsor',
                    dataIndex: 'name',
                    // T-155 — la riga "senza partner" non è uno sponsor: è il
                    // buco. Va letta come tale, non confusa con gli altri.
                    render: (name: string, r) =>
                      r.sponsorId === null ? <Tag color="orange">{name}</Tag> : name,
                  },
                  {
                    title: 'Scontrini',
                    dataIndex: 'receiptCount',
                    align: 'right' as const,
                    sorter: (a, b) => a.receiptCount - b.receiptCount,
                  },
                  {
                    title: 'Tifosi unici',
                    dataIndex: 'uniqueFans',
                    align: 'right' as const,
                    sorter: (a, b) => a.uniqueFans - b.uniqueFans,
                  },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <LineChartCard title="Andamento top 5 sponsor" data={chartData} xKey="date" series={series} />
          </Col>
        </Row>
      )}
    </div>
  );
}
